import React, { useState } from "react";
import { ComponentCategory } from "../types";
import { ExternalLink, MousePointer2, X } from "lucide-react";

interface ComponentSelectorProps {
  categories: ComponentCategory[];
  onCategorySelect: (category: ComponentCategory) => void;
  selectedCategories: Set<string>;
}

export const ComponentSelector: React.FC<ComponentSelectorProps> = ({
  categories,
  onCategorySelect,
  selectedCategories,
}) => {
  const [showFeaturedProduct, setShowFeaturedProduct] = useState(true);

  const borderStyles = () => {
    const root = document.querySelector(".sim-rig-builder");
    if (!root) return {};
    const primaryColor = getComputedStyle(root)
      .getPropertyValue("--srb-primary-color")
      .trim();
    const borderColor = getComputedStyle(root)
      .getPropertyValue("--srb-border-color")
      .trim();

    return { boxShadow: `0 0 0 1px ${borderColor}` };
  };
  return (
    <div className="">
      <div className="srb-flex srb-items-center srb-gap-3 srb-mb-6">
        <MousePointer2 className="srb-text-primary" size={24} />
        <h2 className="srb-text-2xl srb-m-0 srb-font-bold srb-text-main">
          Select Products
        </h2>
      </div>

      <div className="srb-grid srb-grid-cols-2 md:srb-grid-cols-2 srb-gap-3 srb-mb-6">
        {/* featured product, takes up full width */}
        {showFeaturedProduct && (
          <div
            className="srb-col-span-2 srb-p-4 srb-flex srb-flex-row srb-items-center srb-justify-between srb-border-2 hover:srb-bg-hover srb-transition-all srb-duration-200 srb-bg-background srb-relative"
            style={borderStyles()}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="srb-flex srb-flex-row srb-items-center srb-justify-start">
              <img
                src="https://build.readysetsim.com/_next/image?url=%2Fimages%2Fvpgwheel.png&w=1080&q=75"
                alt="VPG Porsche Wheel"
                className="srb-w-[50px] md:srb-w-[100px]"
              />
              <div className="srb-flex srb-flex-col srb-items-start srb-justify-center srb-ml-4 srb-gap-2">
                <p className="srb-m-0 !srb-mb-0 srb-italic srb-text-secondary srb-font-semibold srb-text-sm srb-text-center">
                  Featured Wheel
                </p>
                <p className="srb-font-bold srb-m-0 !srb-mb-0">
                  Porsche 911 Cup
                </p>
                <div className="srb-flex srb-flex-row srb-items-center srb-gap-2 srb-m-0 !srb-mb-0">
                  <p className="srb-m-0 !srb-mb-0 srb-text-xs md:srb-text-md">
                    VPG Sim
                  </p>
                  <div className="srb-w-1 srb-h-1 srb-bg-primary srb-rounded-full"></div>
                  <p className="srb-font-semibold srb-m-0 !srb-mb-0">
                    $1599.00
                  </p>
                </div>
              </div>
            </div>
            <div>
              <a
                className="srb-bg-primary srb-text-xs md:srb-text-md srb-text-secondary srb-no-underline srb-flex srb-items-center srb-gap-2 srb-font-bold srb-px-2 md:srb-px-4 srb-py-2 srb-rounded srb-hover:opacity-90 srb-transition-opacity srb-duration-200"
                href="https://vpgsim.co.uk/sim-racing-wheels/porsche-992-cup-sim-wheel/?tracking=readysetsim"
                target="_blank"
              >
                Buy Now
                <ExternalLink size={16} />
              </a>
            </div>
            {/* Close button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowFeaturedProduct(false);
              }}
              className="srb-absolute srb-top-2 srb-right-2 !srb-bg-transparent srb-border-none srb-cursor-pointer srb-p-1 srb-rounded hover:srb-bg-hover srb-transition-colors"
              aria-label="Close featured product"
            >
              <X size={20} className="srb-text-main" />
            </button>
            {/* Sponsored badge */}
            <div className="srb-absolute srb-bottom-0 srb-left-0 srb-bg-secondary srb-text-background srb-text-[10px] srb-font-semibold srb-px-2 srb-py-1 ">
              Sponsored
            </div>
          </div>
        )}
        {categories.map((category) => {
          const isSelected = selectedCategories.has(category.id);

          // Create dynamic styles based on theme
          const getStyles = () => {
            const root = document.querySelector(".sim-rig-builder");
            if (!root) return {};
            const primaryColor = getComputedStyle(root)
              .getPropertyValue("--srb-primary-color")
              .trim();
            const borderColor = getComputedStyle(root)
              .getPropertyValue("--srb-border-color")
              .trim();

            return isSelected
              ? { boxShadow: `0 0 0 1px ${primaryColor}` }
              : { boxShadow: `0 0 0 1px ${borderColor}` };
          };

          // Check if dark mode for icon inversion
          const isDarkMode =
            document
              .querySelector(".sim-rig-builder")
              ?.getAttribute("data-theme") === "dark";
          const iconClasses = isDarkMode
            ? "srb-w-12 srb-h-12 srb-brightness-0 srb-invert"
            : "srb-w-12 srb-h-12 srb-brightness-0";

          return (
            <div
              key={category.id}
              onClick={() => onCategorySelect(category)}
              className="srb-p-4 srb-flex srb-flex-col srb-items-center srb-justify-center srb-border-2 srb-h-[150px] hover:srb-bg-hover srb-transition-all srb-duration-200 srb-bg-background srb-cursor-pointer srb-relative"
              style={getStyles()}
            >
              <img
                src={category.icon}
                alt={`${category.name} icon`}
                className={iconClasses}
                draggable={false}
              />
              <div className="srb-text-sm srb-font-semibold srb-text-main">
                {category.name}
              </div>
              {isSelected && (
                <div className="srb-text-xs srb-text-primary srb-font-bold srb-absolute srb-top-2 srb-right-2">
                  Selected
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
